import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { projects, accounts, reports, jobs } from '../services/api';
import { confirmDelete, confirmStop, confirmDeltaSync, promptEditUsername, promptAddAccounts, showError } from '../services/alerts';
import ProjectModal from '../components/ProjectModal';

// Mirrors routes/accounts.js's BUSY_STATUSES -- an account actively being
// touched by a migration can be stopped, but not edited or deleted out from
// under it.
const BUSY_STATUSES = ['resetting', 'waiting_restore', 'syncing'];

const ACCOUNT_STATUS = {
  done:            { label: '✓ Done',       color: 'text-good',   bg: 'bg-good/10' },
  syncing:         { label: '⟳ Syncing',    color: 'text-warn',   bg: 'bg-warn/10' },
  failed:          { label: '✗ Failed',     color: 'text-bad',    bg: 'bg-bad/10' },
  pending:         { label: '● Pending',    color: 'text-muted',  bg: 'bg-muted/10' },
  resetting:       { label: '↺ Resetting',  color: 'text-special', bg: 'bg-special/10' },
  waiting_restore: { label: '⏳ Waiting',    color: 'text-accent', bg: 'bg-accent/10' },
  stopped:         { label: '■ Stopped',    color: 'text-dim',    bg: 'bg-dim/10' },
};
const accountMeta = s => ACCOUNT_STATUS[s] || ACCOUNT_STATUS.pending;

const JOB_STATUS = {
  queued:  { label: '● Queued',  color: 'text-muted', bg: 'bg-muted/10' },
  running: { label: '⟳ Running', color: 'text-warn',  bg: 'bg-warn/10' },
  done:    { label: '✓ Done',    color: 'text-good',  bg: 'bg-good/10' },
  failed:  { label: '✗ Failed',  color: 'text-bad',   bg: 'bg-bad/10' },
  stopped: { label: '■ Stopped', color: 'text-dim',   bg: 'bg-dim/10' },
};
const jobMeta = s => JOB_STATUS[s] || JOB_STATUS.queued;

const th = 'border-b border-border2 px-3.5 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-dim';
const td = 'border-b border-border/60 px-3.5 py-2.5 font-mono text-xs';

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [accountList, setAccountList] = useState([]);
  const [jobList, setJobList] = useState([]);
  const [logs, setLogs] = useState([]);
  const [report, setReport] = useState(null);
  const [tab, setTab] = useState('accounts');
  const [showEditModal, setShowEditModal] = useState(false);

  const formatGiB = b => b > 0 ? (b / 1073741824).toFixed(2) + ' GiB' : '—';
  const refreshProject = () => projects.get(id).then(r => setProject(r.data));

  useEffect(() => {
    projects.get(id).then(r => setProject(r.data));
    accounts.list(id).then(r => setAccountList(r.data));
    reports.get(id).then(r => setReport(r.data));
    jobs.list(id).then(r => setJobList(r.data));

    const socket = io();
    socket.emit('join_project', id);
    socket.on('account_update', update => {
      setAccountList(prev => prev.map(a => a.id === update.accountId ? { ...a, ...update } : a));
    });
    socket.on('job_update', job => {
      setJobList(prev => prev.some(j => j.id === job.id)
        ? prev.map(j => j.id === job.id ? job : j)
        : [job, ...prev]);
    });
    socket.on('log', ({ line, type }) => {
      setLogs(prev => [...prev.slice(-200), { line, type, time: new Date().toLocaleTimeString() }]);
    });
    return () => socket.disconnect();
  }, [id]);

  const startAll = async () => {
    await accounts.syncAll(id);
    accounts.list(id).then(r => setAccountList(r.data));
  };

  const handleDelete = async () => {
    if (!(await confirmDelete(project.name))) return;
    try {
      await projects.delete(id);
      navigate('/dashboard');
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to delete project');
    }
  };

  const handleStopProject = async () => {
    if (!(await confirmStop(project.name))) return;
    try {
      await projects.stop(id);
      refreshProject();
      refreshAccounts();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to stop project');
    }
  };

  const refreshAccounts = () => accounts.list(id).then(r => setAccountList(r.data));

  const handleDeltaSync = async () => {
    if (!(await confirmDeltaSync(project.name))) return;
    try {
      await accounts.deltaSync(id);
      refreshProject();
      refreshAccounts();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to start delta sync');
    }
  };

  const handleAddAccounts = async () => {
    const usernames = await promptAddAccounts();
    if (!usernames) return;
    try {
      await accounts.create(id, usernames);
      refreshAccounts();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to add accounts');
    }
  };

  const handleEditAccount = async (account) => {
    const username = await promptEditUsername(account.username);
    if (!username) return;
    try {
      await accounts.update(account.id, { username });
      refreshAccounts();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to update account');
    }
  };

  const handleDeleteAccount = async (account) => {
    if (!(await confirmDelete(account.email))) return;
    try {
      await accounts.remove(account.id);
      refreshAccounts();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to delete account');
    }
  };

  const handleStopAccount = async (account) => {
    if (!(await confirmStop(account.email))) return;
    try {
      await accounts.stop(account.id);
      refreshAccounts();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to stop sync');
    }
  };

  if (!project) return <div className="p-10 font-sans text-muted">Loading…</div>;

  const navItem = (label, action, active) => (
    <div key={label} onClick={action}
      className={`mb-0.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
        active ? 'bg-accent/10 text-accent' : 'cursor-pointer text-muted hover:bg-surface2 hover:text-ink'
      }`}>
      {label}
    </div>
  );

  return (
    <div className="flex h-screen bg-bg font-sans text-ink">
      {/* Sidebar */}
      <div className="flex w-56 shrink-0 flex-col border-r border-border2 bg-surface">
        <div className="flex items-center gap-2.5 border-b border-border2 px-4 py-5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent font-display text-sm font-extrabold text-white">M</div>
          <span className="font-display text-[15px] font-bold">Mail<span className="text-accent">Cutover</span></span>
        </div>
        <nav className="flex-1 p-2 pt-3">
          {navItem('Dashboard', () => navigate('/dashboard'))}
          {navItem('Accounts', () => setTab('accounts'), tab === 'accounts')}
          {navItem('Jobs', () => setTab('jobs'), tab === 'jobs')}
          {navItem('Live Logs', () => setTab('logs'), tab === 'logs')}
          {navItem('Reports', () => navigate(`/projects/${id}/reports`))}
        </nav>
      </div>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Topbar */}
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border2 px-6">
          <button onClick={() => navigate('/dashboard')}
            className="rounded-md border border-border2 px-3 py-1.5 text-xs text-muted transition-colors hover:text-ink">
            ← Back
          </button>
          <div className="flex-1 font-display text-[15px] font-bold">{project.name}</div>
          <button onClick={() => navigate(`/projects/${id}/reports`)}
            className="rounded-md border border-border2 px-3.5 py-1.5 text-sm font-semibold text-muted transition-colors hover:text-ink">
            📊 Report
          </button>
          {project.status === 'running' ? (
            <button onClick={handleStopProject}
              className="rounded-md bg-warn px-4 py-1.5 text-sm font-semibold text-white shadow-[0_0_16px_rgba(245,158,11,.2)] transition-colors hover:opacity-90">
              ■ Stop Project
            </button>
          ) : (
            <button onClick={startAll}
              className="rounded-md bg-accent px-4 py-1.5 text-sm font-semibold text-white shadow-[0_0_16px_rgba(245,47,85,.2)] transition-colors hover:bg-accent2">
              ▶ Sync All Pending
            </button>
          )}
          {(project.status === 'done' || project.status === 'failed') && accountList.some(a => a.status === 'done') && (
            <button onClick={handleDeltaSync}
              className="rounded-md border border-accent/30 px-3.5 py-1.5 text-sm font-semibold text-accent transition-colors hover:bg-accent/10">
              🔄 Run Delta Sync
            </button>
          )}
          <button onClick={() => setShowEditModal(true)} disabled={project.status === 'running'}
            title={project.status === 'running' ? 'Cannot edit while a sync is in progress' : 'Edit project'}
            className="rounded-md border border-border2 px-3.5 py-1.5 text-sm font-semibold text-dim transition-colors
              hover:border-accent/40 hover:text-accent disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border2 disabled:hover:text-dim">
            ✎ Edit
          </button>
          <button onClick={handleDelete} disabled={project.status === 'running'}
            title={project.status === 'running' ? 'Cannot delete while a sync is in progress' : 'Delete project'}
            className="rounded-md border border-border2 px-3.5 py-1.5 text-sm font-semibold text-dim transition-colors
              hover:border-bad/40 hover:text-bad disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border2 disabled:hover:text-dim">
            🗑 Delete
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {/* Stats */}
          <div className="mb-6 grid grid-cols-4 gap-3">
            {[
              ['Done', report?.summary?.done || 0, 'text-good'],
              ['Total', report?.summary?.total || 0, 'text-accent'],
              ['Messages', (report?.summary?.total_messages || 0).toLocaleString(), 'text-ink'],
              ['Data', formatGiB(report?.summary?.total_bytes || 0), 'text-warn'],
            ].map(([label, val, color]) => (
              <div key={label} className="rounded-xl border border-border2 bg-surface px-5 py-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-dim">{label}</div>
                <div className={`font-display text-xl font-bold ${color}`}>{val}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="mb-5 flex gap-1 border-b border-border2">
            {['accounts', 'jobs', 'logs'].map(t => (
              <div key={t} onClick={() => setTab(t)}
                className={`-mb-px cursor-pointer border-b-2 px-4 py-2.5 text-sm font-semibold capitalize transition-colors ${
                  tab === t ? 'border-accent text-accent' : 'border-transparent text-dim hover:text-muted'
                }`}>
                {t === 'accounts' ? `Accounts (${accountList.length})` : t === 'jobs' ? `Jobs (${jobList.length})` : 'Live Logs'}
              </div>
            ))}
          </div>

          {/* Accounts Table */}
          {tab === 'accounts' && (
            <div className="overflow-hidden rounded-xl border border-border2 bg-surface">
              <div className="flex items-center justify-between border-b border-border2 px-4 py-2.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-dim">Mailboxes</span>
                <button onClick={handleAddAccounts}
                  className="rounded-md border border-accent/30 px-3 py-1.5 text-[11px] font-semibold text-accent transition-colors hover:bg-accent/10">
                  + Add Accounts
                </button>
              </div>
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {['Account', 'Status', 'Messages', 'Data', 'Speed', 'Errors', 'Action'].map(h => (
                      <th key={h} className={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accountList.map(a => {
                    const meta = accountMeta(a.status);
                    const busy = BUSY_STATUSES.includes(a.status);
                    return (
                      <tr key={a.id} className="hover:bg-surface2/50">
                        <td className={td}>{a.email}</td>
                        <td className={td}>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.color} ${meta.bg}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className={td}>{a.messages_synced || '—'}</td>
                        <td className={td}>{formatGiB(a.bytes_transferred)}</td>
                        <td className={`${td} text-muted`}>{a.speed_kbps ? `${a.speed_kbps} KiB/s` : '—'}</td>
                        <td className={`${td} ${a.errors > 0 ? 'text-bad' : 'text-good'}`}>{a.errors ?? '—'}</td>
                        <td className={td}>
                          <div className="flex gap-1.5">
                            {busy ? (
                              <button onClick={() => handleStopAccount(a)}
                                className="rounded border border-warn/30 px-2.5 py-1 text-[11px] font-semibold text-warn transition-colors hover:bg-warn/10">
                                ■ Stop
                              </button>
                            ) : (
                              <button onClick={() => accounts.sync(a.id)}
                                className="rounded border border-accent/30 px-2.5 py-1 text-[11px] font-semibold text-accent transition-colors hover:bg-accent/10">
                                ↺ Sync
                              </button>
                            )}
                            <button onClick={() => handleEditAccount(a)} disabled={busy}
                              title={busy ? 'Cannot edit while syncing' : 'Edit username'}
                              className="rounded border border-border2 px-2.5 py-1 text-[11px] font-semibold text-dim transition-colors
                                hover:border-accent/40 hover:text-accent disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border2 disabled:hover:text-dim">
                              ✎
                            </button>
                            <button onClick={() => handleDeleteAccount(a)} disabled={busy}
                              title={busy ? 'Cannot delete while syncing' : 'Delete account'}
                              className="rounded border border-border2 px-2.5 py-1 text-[11px] font-semibold text-dim transition-colors
                                hover:border-bad/40 hover:text-bad disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border2 disabled:hover:text-dim">
                              🗑
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {accountList.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-dim">
                        No accounts found. Add accounts to this project to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Jobs Table */}
          {tab === 'jobs' && (
            <div className="overflow-hidden rounded-xl border border-border2 bg-surface">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {['Account', 'Type', 'Status', 'Started', 'Completed', 'Exit Code'].map(h => (
                      <th key={h} className={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {jobList.map(j => {
                    const meta = jobMeta(j.status);
                    return (
                      <tr key={j.id} className="hover:bg-surface2/50">
                        <td className={td}>{accountList.find(a => a.id === j.account_id)?.email || j.account_id}</td>
                        <td className={`${td} text-muted`}>{j.type}</td>
                        <td className={td}>
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.color} ${meta.bg}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className={`${td} text-muted`}>{j.started_at ? new Date(j.started_at).toLocaleTimeString() : '—'}</td>
                        <td className={`${td} text-muted`}>{j.completed_at ? new Date(j.completed_at).toLocaleTimeString() : '—'}</td>
                        <td className={`${td} ${j.exit_code > 0 ? 'text-bad' : j.exit_code === 0 ? 'text-good' : 'text-dim'}`}>
                          {j.exit_code ?? '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {jobList.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-dim">
                        No jobs yet. Sync an account to queue one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Live Logs */}
          {tab === 'logs' && (
            <div className="max-h-[500px] overflow-y-auto rounded-xl border border-border2 bg-bg2 p-4 font-mono text-[11px] leading-loose">
              {logs.length === 0 ? (
                <div className="text-dim">No logs yet. Start a sync to see live output here.</div>
              ) : (
                logs.map((l, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="shrink-0 text-dim">{l.time}</span>
                    <span className={
                      l.type === 'ok' ? 'text-good' : l.type === 'err' ? 'text-bad' : l.type === 'warn' ? 'text-warn' : 'text-muted'
                    }>{l.line}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {showEditModal && (
        <ProjectModal project={project} onClose={() => setShowEditModal(false)}
          onSaved={() => { setShowEditModal(false); refreshProject(); }} />
      )}
    </div>
  );
}
